import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, Loader2, Phone, ChevronDown } from "lucide-react";
import { api, SELLER_TYPES } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const EMPTY = { name: "", company_name: "", seller_type: "Individual Seller", mobile: "", whatsapp: "", city: "", state: "", notes: "" };

export default function AdminSellers() {
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = () => { setLoading(true); api.get("/admin/sellers").then((r) => setSellers(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => { setEditId(s.id); setForm({ ...EMPTY, ...s }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim() || !form.mobile.trim() || !form.whatsapp.trim()) return toast.error("Name, mobile and WhatsApp required");
    try {
      if (editId) await api.put(`/admin/sellers/${editId}`, form);
      else await api.post("/admin/sellers", form);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => { try { await api.delete(`/admin/sellers/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const toggle = async (s) => {
    if (expanded === s.id) { setExpanded(null); return; }
    const { data } = await api.get(`/admin/sellers/${s.id}`);
    setDetail(data); setExpanded(s.id);
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-extrabold text-slate-900">Sellers</h1>
        <button data-testid="add-seller-btn" onClick={openNew} className="inline-flex items-center gap-1.5 rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white"><Plus size={16} /> Add Seller</button>
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
        <div className="mt-5 space-y-3">
          {sellers.map((s) => (
            <div key={s.id} data-testid={`seller-item-${s.seller_code}`} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-slate-400">{s.seller_code}</span>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">{s.machine_count} machines</span>
                  </div>
                  <p className="font-semibold text-slate-900">{s.name}</p>
                  <p className="text-sm text-slate-500">{s.company_name} • {[s.city, s.state].filter(Boolean).join(", ")}</p>
                  <p className="mt-1 inline-flex items-center gap-1 text-xs text-slate-400"><Phone size={12} /> {s.mobile} • WA: {s.whatsapp}</p>
                </div>
                <button onClick={() => toggle(s)} data-testid={`seller-machines-${s.seller_code}`} className="rounded-lg border border-slate-300 p-2 text-slate-600"><ChevronDown size={16} className={expanded === s.id ? "rotate-180" : ""} /></button>
                <button onClick={() => openEdit(s)} className="rounded-lg border border-slate-300 p-2 text-slate-600"><Edit size={15} /></button>
                <button onClick={() => remove(s.id)} className="rounded-lg border border-red-200 p-2 text-red-500"><Trash2 size={15} /></button>
              </div>
              {expanded === s.id && detail && (
                <div className="mt-3 border-t border-slate-100 pt-3">
                  <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Machines by this seller</p>
                  {detail.machines?.length ? detail.machines.map((m) => (
                    <div key={m.id} className="flex justify-between py-1 text-sm">
                      <span className="text-slate-700">{m.machine_code} — {m.name}</span>
                      <span className="text-slate-400">{m.status}</span>
                    </div>
                  )) : <p className="text-sm text-slate-400">No machines yet.</p>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit" : "Add"} Seller</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Seller Name *</Label><Input data-testid="seller-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Company Name</Label><Input data-testid="seller-company" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} /></div>
            <div>
              <Label>Seller Type</Label>
              <Select value={form.seller_type} onValueChange={(v) => setForm({ ...form, seller_type: v })}>
                <SelectTrigger data-testid="seller-type"><SelectValue /></SelectTrigger>
                <SelectContent>{SELLER_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Mobile *</Label><Input data-testid="seller-mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></div>
              <div><Label>WhatsApp *</Label><Input data-testid="seller-whatsapp" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>City</Label><Input data-testid="seller-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
              <div><Label>State</Label><Input data-testid="seller-state" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></div>
            </div>
            <div><Label>Notes</Label><Textarea data-testid="seller-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <button data-testid="seller-save" onClick={save} className="w-full rounded-full bg-brand py-2.5 font-bold text-white">Save</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
